import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  addVirtualPosIPGFormSchema,
  addVirtualPosIPGValues,
} from "./_schema/add-virtual-pos-ipg.schema";
import FormattedNumberInput from "@/components/v-pos/inputs/formatted-number-input.component";
import { getInitData, numberToPersianWords } from "@/lib/helper";
import { useState } from "react";
import StatusVPosLinkDialog from "../_components/status-vpos-link-dialog.component";
import QRDialog from "./_components/qr-dialog";
import { Link } from "react-router-dom";
import VerifySendLinkDrawer from "./_components/verify-send-link-drawer.component";
import { addVirtualPosResponse } from "@/lib/types/DTO/core";
import LogoutDialog from "./_components/logout-dialog.component";
import InputWithLabel from "@/components/v-pos/inputs/input-with-label.component";
import TextareaWithLabel from "@/components/v-pos/inputs/textarea-with-label.component";
import { FaShop } from "react-icons/fa6";
import { MdHistory } from "react-icons/md";
import logo from "@/assets/images/logo.png";

const VirtualPosIPG = () => {
  const initData = getInitData();

  const [vPosLinkData, setVPosLinkData] = useState<addVirtualPosResponse>();
  const [verifyDialog, setVerifyDialog] = useState<addVirtualPosIPGValues>();

  const form = useForm<addVirtualPosIPGValues>({
    resolver: zodResolver(addVirtualPosIPGFormSchema),
    defaultValues: {
      fullName: "",
      mobile: "",
      amount: "",
      description: "",
    },
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const onSubmit = (values: addVirtualPosIPGValues) => {
    if (vPosLinkData) {
      setVPosLinkData(undefined);
      form.reset();
    } else {
      setVerifyDialog(values);
    }
  };

  // Determine if inputs should be disabled
  const isInputsDisabled = !!vPosLinkData;

  return (
    <div className="w-full p-[20px] flex flex-col min-h-[100dvh]">
      <div className="flex flex-row items-center justify-center gap-2">
        <img src={logo} alt="log" className="w-[14px] mb-5 aspect-[84/100]" />
        <h1 className="mb-4 font-semibold text-center text-primary">کارتخوان مجازی بازار نخل (VPOS)</h1>
      </div>
      <div className="bg-gray-100/80 border border-gray-300/40 rounded-2xl py-4 px-6 mb-[18px] flex justify-between items-center">
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-sm md:text-base ">{initData?.fullName ?? "نامشخص"} عـزیـز، خـوش آمدید!</p>
          <p className="flex flex-row items-center justify-start gap-2 text-primary ">
            <FaShop className="size-4 md:size-5" />
            <p className="font-semibold text-sm md:text-base">{initData?.shopTitle ?? "نامشخص"}</p>
          </p>
        </div>
        <LogoutDialog />
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-[18px] flex-grow"
        >
          <FormField
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FormattedNumberInput
                    inputMode="numeric"
                    label="شماره موبایل مشتری"
                    maxLength={11}
                    disabled={isInputsDisabled}
                    className="font-bold rounded-2xl"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FormattedNumberInput
                    label="مبلغ قابل پرداخت به ریال"
                    inputMode="numeric"
                    maxLength={23}
                    formatType="money"
                    disabled={isInputsDisabled}
                    className="font-bold rounded-2xl"
                    {...field}
                  />
                </FormControl>
                {form.getValues().amount && (<p
                  className={`text-sm text-[#E89B00] font-bold ${field.value.length === 0 && "opacity-0"
                    }`}
                >
                  {numberToPersianWords({
                    value: field.value.slice(0, -1),
                    includeToman: true,
                  })}
                </p>)}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputWithLabel
                    label="نام و نام خانوادگی مشتری (اختیاری)"
                    disabled={isInputsDisabled}
                    className="font-bold !rounded-2xl"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="mb-[16px]">
                <FormControl>
                  <TextareaWithLabel
                    className="field-sizing-fixed font-bold !rounded-2xl"
                    label="توضیحات (اختیاری)"
                    rows={3}
                    disabled={isInputsDisabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="pt-[5px] space-y-[18px]">
            <div className="flex gap-[12px]">
              <Button
                type="submit"
                className={`${vPosLinkData ? "flex-2/3 !text-lg" : "flex-1/1"
                  } font-medium px-[12.5px] rounded-2xl`}
              // disabled={!form.formState.isValid}
              >
                {vPosLinkData ? "انصراف و ارسال لینک جدید" : "ارسال لینک پرداخت "}
              </Button>
              {vPosLinkData && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setVPosLinkData(undefined);
                  }}
                  className="flex-1/3 text-base px-[5px] rounded-2xl"
                >
                  ویرایش تراکنش
                </Button>
              )}
            </div>
            {vPosLinkData && (<StatusVPosLinkDialog linkId={vPosLinkData?.id} />)}

          </div>
          <VerifySendLinkDrawer
            setVPosLinkData={setVPosLinkData}
            verifyDialog={verifyDialog}
            setVerifyDialog={() => setVerifyDialog(undefined)}
          />
        </form>
      </Form>

      <div className="flex justify-between items-center">
        <Link
          to={"/transaction-report"}
          className=" text-primary font-bold flex flex-row items-center border border-primary/50 px-2 py-0.5 rounded-2xl"
        >
          <MdHistory className="size-5" />
          گزارش لینک های ارسال شده
        </Link>
        {vPosLinkData && <QRDialog paymentUrl={vPosLinkData.paymentUrl} />}
      </div>
    </div>
  );
};

export default VirtualPosIPG;
